import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { notifyBadgeUnlock } from "../lib/badges";
import { Plus, Trash2, Target, TrendingUp, Flame, Dumbbell, Loader2, AlertCircle } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import ShareButton from "../components/ShareButton";
import MacroSplitModal from "../components/MacroSplitModal";

const fmtKg = (v) => (v == null ? "—" : `${v}kg`);
const fmtWeeks = (w) => (w == null ? "—" : w >= 52 ? `~${(w / 52).toFixed(1)} yrs` : `${w} wk`);
const todayPlusWeeks = (w) => {
  if (!w) return "—";
  const d = new Date();
  d.setDate(d.getDate() + Math.round(w * 7));
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

function WeightGoalCard({ goal, onDelete, onEditMacros }) {
  if (goal.error) {
    return (
      <div className="border border-white/10 bg-[#121212] p-5" data-testid={`goal-${goal.id}`}>
        <div className="flex items-start gap-3 text-white/70 text-sm">
          <AlertCircle className="w-4 h-4 text-[#FFB020] mt-0.5 shrink-0" />
          <div>
            <div className="font-bold text-white">Weight goal: {goal.target_weight_kg}kg</div>
            <div className="mt-1">{goal.error}</div>
          </div>
        </div>
      </div>
    );
  }
  const isGain = goal.direction === "gain";
  return (
    <article className="border border-white/10 bg-[#121212] p-5" data-testid={`goal-${goal.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="label-eyebrow text-[#6B9BFF] mb-1">
            {isGain ? "Weight gain" : goal.direction === "lose" ? "Weight loss" : "Maintain"}
          </div>
          <div className="font-display text-2xl leading-none">
            {goal.current_weight_kg}kg → <span className="text-[#6B9BFF]">{goal.target_weight_kg}kg</span>
          </div>
          <div className="text-xs text-white/50 mt-2">
            {Math.abs(goal.delta_kg)}kg to go · {Math.abs(goal.pace_kg_per_week)}kg / week · ETA{" "}
            <span className="text-white/80">{todayPlusWeeks(goal.weeks_to_goal)}</span>
          </div>
        </div>
        <button
          onClick={() => onDelete(goal.id)}
          className="text-white/30 hover:text-[#FF3B30]"
          data-testid={`delete-goal-${goal.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {goal.recommended_kcal != null && (
        <div className="flex items-center gap-3 px-4 py-3 border border-[#6B9BFF]/40 bg-[#6B9BFF]/5 mb-4">
          <Flame className="w-4 h-4 text-[#6B9BFF] shrink-0" />
          <div className="text-sm flex-1">
            <span className="font-bold text-white">Eat ~{goal.recommended_kcal} kcal/day</span>
            <span className="text-white/60"> to hit this in {fmtWeeks(goal.weeks_to_goal)}.</span>
            <span className="text-white/40 text-xs ml-2">(TDEE ≈ {goal.tdee_kcal})</span>
          </div>
          <ShareButton
            text={`${isGain ? "Bulking" : goal.direction === "lose" ? "Cutting" : "Maintaining"}: ${goal.current_weight_kg}kg → ${goal.target_weight_kg}kg in ${fmtWeeks(goal.weeks_to_goal)} on DAIONE 🎯`}
            title={`My ${isGain ? "weight gain" : "weight loss"} goal`}
            testid={`share-weight-goal-${goal.id}`}
            card={{
              type: "goal",
              payload: {
                goal_label: isGain ? "DAIONE WEIGHT GAIN" : goal.direction === "lose" ? "DAIONE WEIGHT LOSS" : "DAIONE MAINTAIN",
                direction: goal.direction,
                current_kg: goal.current_weight_kg,
                target_kg: goal.target_weight_kg,
                start_kg: goal.start_weight_kg,
                weeks_to_goal: goal.weeks_to_goal,
                percent_complete: Math.max(0, Math.min(100, Math.round(goal.percent_complete || 0))),
              },
            }}
          />
        </div>
      )}

      {goal.macros && (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="label-eyebrow text-white/40 text-[10px]">Macros</div>
            <button
              type="button"
              onClick={() => onEditMacros && onEditMacros(goal.recommended_kcal)}
              className="text-[10px] uppercase tracking-widest font-bold text-white/40 hover:text-[#6B9BFF]"
              data-testid={`edit-macros-${goal.id}`}
            >
              Edit split{goal.macros.split_source === "manual" ? " ·" : ""}
              {goal.macros.split_source === "manual" && <span className="ml-1 text-[#6B9BFF]">custom</span>}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-4 macro-brand" data-testid={`goal-macros-${goal.id}`}>
          <div className="border border-white/10 bg-[#0a0a0a] p-3">
            <div className="label-eyebrow text-white/40 text-[10px]">Protein</div>
            <div className="font-display text-xl macro-protein">
              {goal.macros.protein_g}<span className="text-sm text-white/40">g</span>
            </div>
            <div className="text-[10px] text-white/40">{goal.macros.protein_per_kg} g/kg</div>
          </div>
          <div className="border border-white/10 bg-[#0a0a0a] p-3">
            <div className="label-eyebrow text-white/40 text-[10px]">Carbs</div>
            <div className="font-display text-xl macro-carbs">
              {goal.macros.carbs_g}<span className="text-sm text-white/40">g</span>
            </div>
            <div className="text-[10px] text-white/40">fills calories</div>
          </div>
          <div className="border border-white/10 bg-[#0a0a0a] p-3">
            <div className="label-eyebrow text-white/40 text-[10px]">Fat</div>
            <div className="font-display text-xl macro-fat">
              {goal.macros.fat_g}<span className="text-sm text-white/40">g</span>
            </div>
            <div className="text-[10px] text-white/40">{goal.macros.fat_per_kg} g/kg</div>
          </div>
          </div>
        </>
      )}

      <div className="h-44 border border-white/5 bg-[#0a0a0a] p-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={goal.projection}>
            <defs>
              <linearGradient id={`grad-${goal.id}`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#6B9BFF" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#6B9BFF" stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="week" stroke="#71717A" fontSize={10} tickFormatter={(v) => `wk ${v}`} />
            <YAxis stroke="#71717A" fontSize={10} domain={["dataMin - 2", "dataMax + 2"]} width={36} />
            <Tooltip
              contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }}
              formatter={(v) => [`${v}kg`, "Projected"]}
              labelFormatter={(l) => `Week ${l}`}
            />
            <Line type="monotone" dataKey="weight" stroke={`url(#grad-${goal.id})`} strokeWidth={3} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
}

function LiftGoalCard({ goal, onDelete }) {
  return (
    <article className="border border-white/10 bg-[#121212] p-5" data-testid={`goal-${goal.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="label-eyebrow text-[#6B9BFF] mb-1">Lift goal</div>
          <div className="font-display text-2xl leading-none">{goal.exercise_name}</div>
          <div className="font-display text-xl text-white/80 mt-1">
            {goal.current_lift_kg}kg → <span className="text-[#6B9BFF]">{goal.target_weight_kg}kg</span>
          </div>
          <div className="text-xs text-white/50 mt-2">
            {Math.abs(goal.delta_kg)}kg to go · {Math.abs(goal.pace_kg_per_week)}kg / week · ETA{" "}
            <span className="text-white/80">{todayPlusWeeks(goal.weeks_to_goal)}</span>
            {!goal.has_pr_baseline && (
              <span className="ml-2 text-[#FFB020]">· no PR logged yet</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <ShareButton
            text={`Going for ${goal.target_weight_kg}kg on ${goal.exercise_name} (currently ${goal.current_lift_kg}kg) on DAIONE 💪`}
            title={`${goal.exercise_name} target`}
            testid={`share-lift-goal-${goal.id}`}
            card={{
              type: "goal",
              payload: {
                goal_label: `${goal.exercise_name.toUpperCase()} TARGET`,
                direction: "gain",
                current_kg: goal.current_lift_kg,
                target_kg: goal.target_weight_kg,
                start_kg: goal.start_lift_kg,
                weeks_to_goal: goal.weeks_to_goal,
                percent_complete: Math.max(0, Math.min(100, Math.round(goal.percent_complete || 0))),
              },
            }}
          />
          <button
            onClick={() => onDelete(goal.id)}
            className="text-white/30 hover:text-[#FF3B30]"
            data-testid={`delete-goal-${goal.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="h-44 border border-white/5 bg-[#0a0a0a] p-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={goal.projection}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="week" stroke="#71717A" fontSize={10} tickFormatter={(v) => `wk ${v}`} />
            <YAxis stroke="#71717A" fontSize={10} domain={["dataMin - 5", "dataMax + 5"]} width={36} />
            <Tooltip
              contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", color: "#fff", fontSize: 12 }}
              formatter={(v) => [`${v}kg`, "Projected"]}
              labelFormatter={(l) => `Week ${l}`}
            />
            <Line type="monotone" dataKey="weight" stroke="#6B9BFF" strokeWidth={3} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
}

export default function GoalsPage() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [tdee, setTdee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Weight form
  const [wTarget, setWTarget] = useState("");
  const [wDate, setWDate] = useState("");
  const [wPace, setWPace] = useState("");

  // Macro split modal trigger
  const [macroModalOpen, setMacroModalOpen] = useState(false);
  const [macroKcal, setMacroKcal] = useState(2200);

  // Lift form
  const [lExercise, setLExercise] = useState("");
  const [lTarget, setLTarget] = useState("");
  const [lDate, setLDate] = useState("");
  const [lStart, setLStart] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/goals");
      setGoals(data.goals || []);
      setTdee(data.tdee_kcal);
    } catch (err) {
      console.error("goals load failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (payload, resetFn) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/goals", payload);
      resetFn?.();
      await load();
      toast.success("Goal added");
      notifyBadgeUnlock(data?.badge_unlocked);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save goal");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/goals/${id}`);
      setGoals((g) => g.filter((x) => x.id !== id));
      toast.success("Goal removed");
    } catch {
      toast.error("Failed to remove");
    }
  };

  const submitWeight = (e) => {
    e.preventDefault();
    if (!wTarget) return toast.error("Enter a target weight");
    const payload = { type: "weight", target_weight_kg: Number(wTarget) };
    if (wDate) payload.target_date = wDate;
    if (wPace) payload.pace_kg_per_week = Number(wPace);
    submit(payload, () => {
      setWTarget("");
      setWDate("");
      setWPace("");
    });
  };

  const submitLift = (e) => {
    e.preventDefault();
    if (!lExercise || !lTarget) return toast.error("Exercise and target are required");
    const payload = {
      type: "lift",
      exercise_name: lExercise,
      target_weight_kg: Number(lTarget),
    };
    if (lDate) payload.target_date = lDate;
    if (lStart) payload.starting_weight_kg = Number(lStart);
    submit(payload, () => {
      setLExercise("");
      setLTarget("");
      setLDate("");
      setLStart("");
    });
  };

  const weightGoals = useMemo(() => goals.filter((g) => g.type === "weight"), [goals]);
  const liftGoals = useMemo(() => goals.filter((g) => g.type === "lift"), [goals]);

  const profileMissing = !user?.weight_kg || !user?.height_cm || !user?.age;

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-up" data-testid="goals-page">
      <div className="mb-10">
        <div className="label-eyebrow text-[#6B9BFF] mb-2 flex items-center gap-2">
          <Target className="w-3.5 h-3.5" /> Goals
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-none">Aim. Adjust. Achieve.</h1>
        <p className="text-white/60 mt-3 max-w-2xl">
          Set body weight or lift targets — we calculate the timeline, recommended calories, and chart
          the path. Adjust the pace anytime.
        </p>
        {tdee && (
          <div className="mt-4 inline-block border border-white/10 px-4 py-2 text-sm">
            <span className="text-white/50 mr-2">Your estimated TDEE</span>
            <span className="font-display text-xl text-[#6B9BFF]">{tdee}</span>
            <span className="text-white/50 ml-1">kcal/day</span>
          </div>
        )}
        {profileMissing && (
          <div className="mt-4 flex items-start gap-2 text-sm text-[#FFB020]">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Complete your weight, height and age in <a className="underline" href="/app/profile">profile</a> to unlock calorie recommendations.
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/50 text-sm py-12">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading goals…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Weight goals */}
          <section data-testid="weight-section">
            <div className="flex items-center gap-2 mb-4">
              <Flame className="w-4 h-4 text-[#6B9BFF]" />
              <h2 className="font-display text-2xl">Weight</h2>
            </div>

            <form onSubmit={submitWeight} className="tracing-border p-5 mb-5" data-testid="weight-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-eyebrow text-white/50 block mb-2">Target (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    required
                    value={wTarget}
                    onChange={(e) => setWTarget(e.target.value)}
                    placeholder="80"
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#6B9BFF]"
                    data-testid="weight-target-input"
                  />
                </div>
                <div>
                  <label className="label-eyebrow text-white/50 block mb-2">Target date</label>
                  <input
                    type="date"
                    value={wDate}
                    onChange={(e) => setWDate(e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#6B9BFF]"
                    data-testid="weight-date-input"
                  />
                </div>
                <div className="col-span-2">
                  <label className="label-eyebrow text-white/50 block mb-2">
                    Or set pace (kg / week) <span className="text-white/30">— optional</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="-1"
                    max="1"
                    value={wPace}
                    onChange={(e) => setWPace(e.target.value)}
                    placeholder="0.5 (safe range -1 to 1)"
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#6B9BFF]"
                    data-testid="weight-pace-input"
                  />
                </div>
              </div>
              <button
                disabled={submitting}
                type="submit"
                className="mt-4 w-full bg-[#6B9BFF] text-black font-black uppercase tracking-widest py-3 hover:bg-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="weight-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add weight goal
              </button>
            </form>

            <div className="space-y-4">
              {weightGoals.length === 0 ? (
                <div className="text-sm text-white/40 italic text-center py-6">
                  No weight goals yet — add one above.
                </div>
              ) : (
                weightGoals.map((g) => <WeightGoalCard key={g.id} goal={g} onDelete={remove} onEditMacros={(k) => { setMacroKcal(k || 2200); setMacroModalOpen(true); }} />)
              )}
            </div>
          </section>

          {/* Lift goals */}
          <section data-testid="lift-section">
            <div className="flex items-center gap-2 mb-4">
              <Dumbbell className="w-4 h-4 text-[#6B9BFF]" />
              <h2 className="font-display text-2xl">Lifts</h2>
            </div>

            <form onSubmit={submitLift} className="tracing-border p-5 mb-5" data-testid="lift-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="label-eyebrow text-white/50 block mb-2">Exercise</label>
                  <input
                    required
                    value={lExercise}
                    onChange={(e) => setLExercise(e.target.value)}
                    placeholder="Bench Press"
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#6B9BFF]"
                    data-testid="lift-exercise-input"
                  />
                  <div className="text-[10px] text-white/40 mt-1">
                    Uses your logged 1RM as the starting point.
                  </div>
                </div>
                <div>
                  <label className="label-eyebrow text-white/50 block mb-2">Target 1RM (kg)</label>
                  <input
                    type="number"
                    step="0.5"
                    required
                    value={lTarget}
                    onChange={(e) => setLTarget(e.target.value)}
                    placeholder="100"
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#6B9BFF]"
                    data-testid="lift-target-input"
                  />
                </div>
                <div>
                  <label className="label-eyebrow text-white/50 block mb-2">Target date</label>
                  <input
                    type="date"
                    value={lDate}
                    onChange={(e) => setLDate(e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#6B9BFF]"
                    data-testid="lift-date-input"
                  />
                </div>
                <div className="col-span-2">
                  <label className="label-eyebrow text-white/50 block mb-2">
                    Starting weight <span className="text-white/30">— optional, if no PR yet</span>
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={lStart}
                    onChange={(e) => setLStart(e.target.value)}
                    placeholder="60"
                    className="w-full bg-[#1A1A1A] border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[#6B9BFF]"
                    data-testid="lift-start-input"
                  />
                </div>
              </div>
              <button
                disabled={submitting}
                type="submit"
                className="mt-4 w-full bg-[#6B9BFF] text-black font-black uppercase tracking-widest py-3 hover:bg-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="lift-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Add lift goal
              </button>
            </form>

            <div className="space-y-4">
              {liftGoals.length === 0 ? (
                <div className="text-sm text-white/40 italic text-center py-6">
                  No lift goals yet — add one above.
                </div>
              ) : (
                liftGoals.map((g) => <LiftGoalCard key={g.id} goal={g} onDelete={remove} />)
              )}
            </div>
          </section>
        </div>
      )}
      <MacroSplitModal
        open={macroModalOpen}
        onOpenChange={(o) => {
          setMacroModalOpen(o);
          if (!o) load(); // refresh goals so updated macros render
        }}
        kcalTarget={macroKcal}
      />
    </div>
  );
}
